import { useState, useEffect } from "react";
import { Question } from "@/types/activity";

const CACHE_EXPIRATION = 7 * 24 * 60 * 60 * 1000; // 7 Days

const cacheWithServiceWorker = async (
  url: string,
  db: IDBDatabase
): Promise<string> => {
  const cache = await caches.open("media-cache");
  const cachedResponse = await cache.match(url);

  if (cachedResponse) {
    const blob = await cachedResponse.blob();
    return URL.createObjectURL(blob);
  }

  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error("Network response was not ok");

    await cache.put(url, response.clone());

    const blob = await response.blob();
    return URL.createObjectURL(blob);
  } catch (error) {
    console.warn("Falling back to IndexedDB due to error:", url, error);
    return cacheFile(url, db);
  }
};

const cacheFile = async (url: string, db: IDBDatabase): Promise<string> => {
  const fileName = url;
  const transaction = db.transaction(["files"], "readonly");
  const objectStore = transaction.objectStore("files");

  const cachedFile = await new Promise<{ blob: Blob; timestamp: number }>(
    (resolve, reject) => {
      const request = objectStore.get(fileName);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () =>
        reject(new Error("Error fetching file from IndexedDB"));
    }
  );

  if (cachedFile) {
    const timestamp = cachedFile.timestamp;
    if (Date.now() - timestamp < CACHE_EXPIRATION) {
      return URL.createObjectURL(cachedFile.blob);
    } else {
      await deleteFile(db, fileName);
      return fetchAndCacheFile(url, fileName, db);
    }
  } else {
    return fetchAndCacheFile(url, fileName, db);
  }
};

const openDB = (): Promise<IDBDatabase> => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open("mediaCache", 1);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains("files")) {
        const store = db.createObjectStore("files", { keyPath: "name" });
        store.createIndex("name", "name", { unique: true });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(new Error("Error opening IndexedDB"));
  });
};

const fetchAndCacheFile = async (
  url: string,
  fileName: string,
  db: IDBDatabase
): Promise<string> => {
  try {
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`Fetch failed with status: ${response.status}`);
    }

    const contentType =
      response.headers.get("Content-Type") || guessMimeType(url);
    const blob = await response.blob();
    const correctedBlob = new Blob([blob], { type: contentType });
    const timestamp = Date.now();

    const transaction = db.transaction(["files"], "readwrite");
    const objectStore = transaction.objectStore("files");
    await new Promise<void>((resolve, reject) => {
      const request = objectStore.put({
        name: fileName,
        blob: correctedBlob,
        timestamp,
      });
      request.onsuccess = () => resolve();
      request.onerror = () =>
        reject(new Error("Error storing file in IndexedDB"));
    });

    return URL.createObjectURL(correctedBlob);
  } catch (error) {
    return url;
  }
};

const guessMimeType = (url: string): string => {
  const extension = url.split(".").pop()?.toLowerCase();
  switch (extension) {
    case "mp4":
      return "video/mp4";
    case "mp3":
      return "audio/mpeg";
    case "wav":
      return "audio/wav";
    case "jpg":
    case "jpeg":
      return "image/jpeg";
    case "png":
      return "image/png";
    default:
      return "application/octet-stream";
  }
};

const deleteFile = async (db: IDBDatabase, fileName: string): Promise<void> => {
  const transaction = db.transaction(["files"], "readwrite");
  const objectStore = transaction.objectStore("files");
  await new Promise<void>((resolve, reject) => {
    const request = objectStore.delete(fileName);
    request.onsuccess = () => resolve();
    request.onerror = () =>
      reject(new Error("Error deleting file from IndexedDB"));
  });
};

export const useCachedActivities = (data: Question[] | null) => {
  const [cachedActivities, setCachedActivities] = useState<Question[]>([]);

  useEffect(() => {
    let db: IDBDatabase | null = null;
    let isMounted = true;

    const cacheMediaFiles = async () => {
      if (!data?.length) return;

      try {
        db = await openDB();
        if (!db) throw new Error("Failed to open IndexedDB");

        const newActivities: Question[] = await Promise.all(
          data.map(async (question) => {
            if (!isMounted) throw new Error("Component unmounted");

            const cloned = structuredClone(question);

            const cache = async (url: string | undefined) => {
              if (!url) return undefined;
              return await cacheWithServiceWorker(url, db!);
            };

            switch (cloned.activityType) {
              case "VideoContentActivity":
                if (cloned.question?.videoUrl) {
                  cloned.question.videoUrl = await cache(
                    cloned.question.videoUrl
                  );
                }
                cloned.options = await Promise.all(
                  cloned.options.map(async (opt: any) => ({
                    ...opt,
                    audioUrl: await cache(opt.audioUrl),
                  }))
                );
                break;

              case "SelectCorrectImageActivity":
              case "TapInWhatYouHear":
              case "MatchTheCorrectWord":
                if (cloned.question?.audioUrl) {
                  cloned.question.audioUrl = await cache(
                    cloned.question.audioUrl
                  );
                }
                cloned.options = await Promise.all(
                  cloned.options.map(async (opt: any) => ({
                    ...opt,
                    audioUrl: await cache(opt.audioUrl),
                    imageUrl: await cache(opt.imageUrl),
                  }))
                );
                break;

              case "FillTheGapActivity":
                if (cloned.audioUrl) {
                  cloned.audioUrl = await cache(cloned.audioUrl);
                }
                if (cloned.imageUrl) {
                  cloned.imageUrl = await cache(cloned.imageUrl);
                }
                cloned.options = await Promise.all(
                  cloned.options.map(async (opt: any) => ({
                    ...opt,
                    audioUrl: await cache(opt.audioUrl),
                  }))
                );
                break;

              case "CrossWordActivity":
                if (Array.isArray(cloned.question)) {
                  cloned.question = await Promise.all(
                    cloned.question.map(async (q: any) => ({
                      ...q,
                      audioUrl: await cache(q.audioUrl),
                    }))
                  );
                }
                cloned.options = await Promise.all(
                  cloned.options.map(async (opt: any) => ({
                    ...opt,
                    audioUrl: await cache(opt.audioUrl),
                  }))
                );
                break;

              case "ChatActivity":
                cloned.series = await Promise.all(
                  cloned.series.map(async (series: any) => {
                    const clonedSeries = { ...series };

                    if (clonedSeries.question?.audioUrl) {
                      clonedSeries.question = {
                        ...clonedSeries.question,
                        audioUrl: await cache(clonedSeries.question.audioUrl),
                      };
                    }
                    if (clonedSeries.question?.videoUrl) {
                      clonedSeries.question = {
                        ...clonedSeries.question,
                        videoUrl: await cache(clonedSeries.question.videoUrl),
                      };
                    }

                    clonedSeries.options = await Promise.all(
                      (clonedSeries.options || []).map(async (opt: any) => {
                        const audioUrl =
                          opt.audio && opt.audio !== ""
                            ? await cache(opt.audio)
                            : undefined;
                        const imageUrl = opt.imageUrl
                          ? await cache(opt.imageUrl)
                          : undefined;
                        const videoUrl = opt.videoUrl
                          ? await cache(opt.videoUrl)
                          : undefined;
                        return {
                          ...opt,
                          audioUrl,
                          imageUrl,
                          videoUrl,
                        };
                      })
                    );

                    // Remove original `audio` key
                    clonedSeries.options = clonedSeries.options.map(
                      ({ audio, ...rest }: { audio: any }) => rest
                    );

                    return clonedSeries;
                  })
                );
                break;
            }

            return cloned;
          })
        );

        if (isMounted) {
          setCachedActivities(newActivities);
        }
      } catch (error) {
        console.error("Error caching activities:", error);
        if (isMounted) {
          setCachedActivities(data ?? []);
        }
      }
    };

    cacheMediaFiles();

    return () => {
      isMounted = false;
      if (db) {
        setTimeout(() => {
          db?.close();
        }, 100);
      }
    };
  }, [data]);

  return cachedActivities;
};
